import _ from 'lodash'
import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'
import * as blueprint from '@adc/blueprint'

const SEARCH_USER_FRAGMENT = gql`
  fragment SearchUserFragment on User {
    user_id
    party_id
    email
    first_name
    last_name
    party {
      phones {
        phone_id
        phone_usage
        number
        opt_in
        verified
      }
    }
    communication_preference {
      urn
      rules {
        channel
        audience
        opt_status
      }
    }
    saved_searches(source: ADC, sort: "createdDate,desc") {
      saved_search_id
      saved_search_name
      parameters
      sms_notifications_enabled
    }
  }
`

export const fetchUserDetails = async (
  req,
  { shouldFetchAdaptiveNav = false } = {}
) =>
  fetch
    .forAuctionGraph(
      {
        query: gql`
          ${SEARCH_USER_FRAGMENT}
          ${blueprint.user.fragments.buyingPreferenceFields.definition}
          ${blueprint.user.fragments.buyingPreferences.definition}
          ${blueprint.user.fragments.adaptiveNavigation.definition}

          query resiSearch_fetchCurrentUser($shouldFetchAdaptiveNav: Boolean!) {
            current_user {
              ...SearchUserFragment
              ${blueprint.user.fragments.buyingPreferences.reference}
              ${blueprint.user.fragments.adaptiveNavigation.reference} @include(if: $shouldFetchAdaptiveNav)
            }
          }
        `,
        variables: {
          shouldFetchAdaptiveNav,
        },
      },
      { req }
    )
    .then(res => {
      const errors = _.get(res, 'body.errors[0]')

      // We allow partial errors so long as some information about a user was returned
      if (errors && !res.body.data?.current_user) throw errors

      return res.body.data.current_user
    })

/**
 * This is a major departure from previous data fetching strategies in two key ways:
 *
 * 1) Instead of needlessly serializing calls that don't depend on each other make a single request and both are
 *    executed in parallel.
 *
 * 2) Instead of saying 'fetch all the data in the system' and matching to a tiny fraction we have in the UI at any
 *    given time we can make that operation more declarative. Here we are saying 'For all the listings I have currently
 *    loaded in the UI, tell me if this new user has currently saved any of them' in a way that bypasses the source of
 *    pagination bugs and ends up being a more natural representation of what the application is trying to do.
 *
 */
export const fetchDataForNewlyAuthenticatedUser = ({
  propertyIds,
  shouldFetchAdaptiveNav = false,
}) =>
  fetch
    .forAuctionGraph({
      query: `
${SEARCH_USER_FRAGMENT}
${blueprint.user.fragments.adaptiveNavigation.definition}

query resiSearch_fetchDataForNewlyAuthenticatedUser ($propertyIds: [ID!]!, $shouldFetchAdaptiveNav: Boolean!) {
  current_user {
    ...SearchUserFragment
    ${blueprint.user.fragments.adaptiveNavigation.reference} @include(if: $shouldFetchAdaptiveNav)

    saved_property_lookup(propertyIds: $propertyIds)
  }
}
  `,
      variables: { propertyIds, shouldFetchAdaptiveNav },
    })
    .then(res => res.body)

export const fetchUserSavedSearches = req =>
  fetch
    .forAuctionGraph(
      {
        query: `
  query resiSearch_fetchCurrentUserSavedSearches {
    current_user {
      saved_searches(source: ADC, sort: "createdDate,desc") {
        saved_search_id
        saved_search_name
        parameters
      }
    }
  }
        `,
      },
      { req }
    )
    .then(res => {
      const errors = _.get(res, 'body.errors[0]')
      if (errors) throw errors
      return res
    })
    .then(res => _.get(res, 'body.data.current_user.saved_searches', []))
